// CampagneFormModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import "../../RessourcesStyles/CampagneFormModal.css"; // ✅ style Campagne dédié (Attestation-like)

import MultiSelectDropdown from "../../../../../Ui/UiJs/MultiSelectDropdown";
import UsersListModal from "./UsersListModal";

// actions (adapte les chemins selon ton projet)
import {
    fetchCampagnesRequest,
    createCampagneRequest,
    updateCampagneRequest,
} from "../../../../../redux/actions/campagnesActions";
import { fetchCataloguesRequest } from "../../../../../redux/actions/cataloguesActions";
import { getAllEntitesRequest } from "../../../../../redux/actions/entitesActions";
import { getAllJobsRequest } from "../../../../../redux/actions/Jobactions";
import { getAllUsersRequest } from "../../../../../redux/actions/userActions";

const toDateInputValue = (iso) => (iso ? String(iso).split("T")[0] : "");

const uniqNumbers = (arr) =>
    Array.from(
        new Set((arr || []).map((x) => Number(x)).filter((x) => !Number.isNaN(x)))
    );

const splitAxes = (axesStr) => {
    if (!axesStr) return [];
    return String(axesStr)
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
};

const CampagneFormModal = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const cid = id ? Number(id) : null;

    const { list: campagnes = [], loading: campagnesLoading } = useSelector(
        (s) => s.campagnes || {}
    );
    const { catalogues = [] } = useSelector((s) => s.catalogues || {});
    const { entites = [] } = useSelector((s) => s.entites || {});
    const { jobs = [] } = useSelector((s) => s.jobs || {});
    const { users = [] } = useSelector((s) => s.user || {});


    // ✅ Module actif (déduit automatiquement le type de campagne)
    const selectedRole = useSelector((state) => state.role?.selectedRole);
    const isRecrutementModule = useMemo(() => {
        const name = String(selectedRole?.moduleName || "").toLowerCase().trim();
        return name.includes("recrut");
    }, [selectedRole]);
    const isFormationModule = useMemo(() => !isRecrutementModule, [isRecrutementModule]);
    const isSkillsModule = useMemo(() => {
        return String(selectedRole?.moduleName || "")
            .toLowerCase()
            .includes("comp"); // ou "skill" selon la valeur réelle
    }, [selectedRole]);

    const campagne = useMemo(() => {
        if (!cid) return null;
        return (campagnes || []).find((c) => Number(c.id) === Number(cid)) || null;
    }, [cid, campagnes]);

    // Modal Users
    const [openUsersModal, setOpenUsersModal] = useState(false);

    // Form
    const [form, setForm] = useState({
        nom: "",
        reference: "",
        description: "",
        dateDebut: "",
        dateFin: "",
        anneeCampagne: new Date().getFullYear(),
        nombreMaxFormations: 1,

        niveauValidation: "MANAGER", // MANAGER | RESPONSABLE_FORMATION | LES_DEUX
        envoiEmails: "INSTANTANE", // INSTANTANE | DIFFERE | SANS_EMAIL
        dateEnvoiEmails: "",

        planificationRelance: "false",
        dateRelance: "",
        dateRelance2: "",
        dateRelance3: "",

        status: true,
    });

    // Population & visibilité
    const [filterMode, setFilterMode] = useState("IN"); // IN | NOT_IN
    const [managerLevelFilter, setManagerLevelFilter] = useState(""); // "" | "n+1" | "n+2"
    const [entiteIds, setEntiteIds] = useState([]);
    const [posteIds, setPosteIds] = useState([]);

    // Users selection
    const [usersState, setUsersState] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [initialUserIds, setInitialUserIds] = useState([]);

    // Catalogues
    const [catalogueIds, setCatalogueIds] = useState([]);


    // ✅ Recrutement - limites
    const [recruitmentLimits, setRecruitmentLimits] = useState([
        { typeLimite: "Global", entiteId: "", limite: "" },
    ]);

    // Axes
    const [axeToAdd, setAxeToAdd] = useState("");
    const [axesArray, setAxesArray] = useState([]);

    // Budgets
    const [typeBudget, setTypeBudget] = useState("Global"); // Global | Par entité
    const [currency, setCurrency] = useState("MAD");
    const [budgetGlobal, setBudgetGlobal] = useState("");
    const [budgetsByEntite, setBudgetsByEntite] = useState([]);

    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(fetchCataloguesRequest());
        dispatch(getAllEntitesRequest());
        dispatch(getAllJobsRequest());
        dispatch(getAllUsersRequest());
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    // Helpers
    const toggleId = (arr, idv) => {
        const idn = Number(idv);
        return arr.includes(idn) ? arr.filter((x) => x !== idn) : arr.concat([idn]);
    };

    const addBudgetRow = () => {
        setBudgetsByEntite((prev) => prev.concat([{ entiteId: "", currency, montant: "" }]));
    };
    const removeBudgetRow = (idx) => {
        setBudgetsByEntite((prev) => prev.filter((_, i) => i !== idx));
    };
    const updateBudgetRow = (idx, patch) => {
        setBudgetsByEntite((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
    };

    // Hydrate create/edit
    useEffect(() => {
        if (!cid) {
            setForm({
                nom: "",
                reference: "",
                description: "",
                dateDebut: "",
                dateFin: "",
                anneeCampagne: new Date().getFullYear(),
                nombreMaxFormations: 1,

                niveauValidation: "MANAGER",
                envoiEmails: "INSTANTANE",
                dateEnvoiEmails: "",

                planificationRelance: "false",
                dateRelance: "",
                dateRelance2: "",
                dateRelance3: "",

                status: true,
            });

            setFilterMode("IN");
            setManagerLevelFilter("");
            setEntiteIds([]);
            setPosteIds([]);
            setCatalogueIds([]);
            setRecruitmentLimits([{ typeLimite: "Global", entiteId: "", limite: "" }]);

            setTypeBudget("Global");
            setCurrency("MAD");
            setBudgetGlobal("");
            setBudgetsByEntite([]);

            setUsersState([]);
            setSelectedUsers([]);
            setInitialUserIds([]);

            setAxeToAdd("");
            setAxesArray([]);

            setErrors({});
            return;
        }

        if (!campagne) return;

        setForm((p) => ({
            ...p,
            nom: campagne.nom || "",
            reference: campagne.reference || "",
            description: campagne.description || "",
            dateDebut: toDateInputValue(campagne.dateDebut),
            dateFin: toDateInputValue(campagne.dateFin),
            anneeCampagne: Number(campagne.anneeCampagne || p.anneeCampagne),
            nombreMaxFormations: Number(campagne.nombreMaxFormations || 1),

            niveauValidation: campagne.validation || campagne.niveauValidation || "MANAGER",
            envoiEmails: campagne.envoiEmails || "INSTANTANE",
            dateEnvoiEmails: toDateInputValue(campagne.dateEnvoiEmails),

            planificationRelance: String(campagne.planificationRelance ?? "false"),
            dateRelance: toDateInputValue(campagne.dateRelance),
            dateRelance2: toDateInputValue(campagne.dateRelance2),
            dateRelance3: toDateInputValue(campagne.dateRelance3),

            status: Boolean(campagne.status ?? true),
        }));

        setFilterMode(campagne.filterMode === "NOT_IN" ? "NOT_IN" : (campagne.filterMode || "IN"));
        setManagerLevelFilter(campagne.managerLevelFilter || "");
        setEntiteIds(uniqNumbers(campagne.entiteIds || []));
        setPosteIds(uniqNumbers(campagne.posteIds || []));
        setCatalogueIds(uniqNumbers(campagne.catalogueIds || []));
        setRecruitmentLimits(
            Array.isArray(campagne.recruitmentLimits) && campagne.recruitmentLimits.length
                ? campagne.recruitmentLimits.map((r) => ({
                    typeLimite: r.typeLimite || "Global",
                    entiteId: r.entiteId ?? "",
                    limite: r.limite ?? "",
                }))
                : [{ typeLimite: "Global", entiteId: "", limite: "" }]
        );

        setInitialUserIds(uniqNumbers(campagne.userIds || []));

        setAxeToAdd("");
        setAxesArray(splitAxes(campagne.axes));

        const bList = campagne.budgets || [];
        const b0 = bList[0];
        if (b0) {
            setTypeBudget(b0.typeBudget || "Global");
            setCurrency(b0.currency || "MAD");

            if ((b0.typeBudget || "") === "Global") {
                setBudgetGlobal(String(b0.montant ?? ""));
                setBudgetsByEntite([]);
            } else {
                setBudgetGlobal("");
                setBudgetsByEntite(
                    bList.map((b) => ({
                        entiteId: b.entiteId ?? b.entite?.id ?? "",
                        currency: b.currency || "MAD",
                        montant: b.montant != null ? String(b.montant) : "",
                    }))
                );
            }
        } else {
            setTypeBudget("Global");
            setCurrency("MAD");
            setBudgetGlobal("");
            setBudgetsByEntite([]);
        }
    }, [cid, campagne]);

    // Sync usersState based on all users + initialUserIds + preserve checked
    useEffect(() => {
        const prevMap = new Map((usersState || []).map((u) => [Number(u.id), !!u.checked]));
        const initSet = new Set((initialUserIds || []).map((x) => Number(x)));

        const next = (users || []).map((u) => {
            const uid = Number(u.id);
            const checked = prevMap.has(uid) ? prevMap.get(uid) : initSet.has(uid);
            return { ...u, checked: !!checked };
        });

        setUsersState(next);
        setSelectedUsers(next.filter((u) => u.checked));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, initialUserIds]);

    const selectedLabel = useMemo(() => {
        if (!managerLevelFilter) return "Choisis d’abord un niveau manager (n+1 / n+2) pour sélectionner des users.";
        if (!selectedUsers.length) return "Aucun collaborateur sélectionné.";
        return `${selectedUsers.length} collaborateur(s) sélectionné(s).`;
    }, [managerLevelFilter, selectedUsers]);

    const populationCount = useMemo(() => (usersState || []).length, [usersState]);

    const validate = () => {
        const e = {};
        if (!String(form.nom || "").trim()) e.nom = "Obligatoire";
        if (!String(form.description || "").trim()) e.description = "Obligatoire";
        if (isFormationModule && !catalogueIds.length) e.catalogues = "Sélectionne au moins un catalogue";

        // ✅ Recrutement: limites
        if (isRecrutementModule) {
            if (!recruitmentLimits || recruitmentLimits.length === 0) {
                e.recruitmentLimits = "Ajoute au moins une limite";
            } else {
                const invalid = recruitmentLimits.some((r) => {
                    const lim = Number(r.limite);
                    if (!r.typeLimite) return true;
                    if (Number.isNaN(lim) || lim < 0) return true; // accepte 0 si tu veux
                    if (String(r.typeLimite).toLowerCase().includes("entit") && !r.entiteId) return true;
                    return false;
                });
                if (invalid) e.recruitmentLimits = "Limites invalides (type + limite, entité obligatoire si 'Par entité')";
            }
        }

        if (typeBudget === "Global") {
            const bg = Number(budgetGlobal);
            if (Number.isNaN(bg) || bg <= 0) e.budgetGlobal = "Budget global invalide";
        } else {
            const clean = (budgetsByEntite || []).filter(
                (r) => String(r.entiteId || "").trim() !== "" || String(r.montant || "").trim() !== ""
            );

            if (!clean.length) {
                e.budgetsByEntite = "Ajoute au moins un budget par entité";
            } else {
                const hasInvalid = clean.some(
                    (r) => !r.entiteId || Number(r.montant) <= 0 || Number.isNaN(Number(r.montant))
                );
                if (hasInvalid) e.budgetsByEntite = "Budget par entité invalide (entité + montant > 0)";
            }
        }

        if (form.envoiEmails === "DIFFERE" && !String(form.dateEnvoiEmails || "").trim()) {
            e.dateEnvoiEmails = "Date d'envoi obligatoire (Différé)";
        }

        if (String(form.planificationRelance) === "true" && !String(form.dateRelance || "").trim()) {
            e.dateRelance = "Date relance 1 obligatoire";
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const buildPayload = () => {
        const budgets =
            typeBudget === "Global"
                ? [{ montant: Number(budgetGlobal), currency, typeBudget: "Global" }]
                : (budgetsByEntite || [])
                    .filter((r) => r.entiteId && String(r.montant || "").trim() !== "")
                    .map((r) => ({
                        entiteId: Number(r.entiteId),
                        montant: Number(r.montant),
                        currency: r.currency || currency,
                        typeBudget: "Par entité",
                    }));

        const axes = (axesArray || []).length ? axesArray.join("\n") : "";

        return {
            nom: form.nom,
            reference: form.reference,
            description: form.description,
            dateDebut: form.dateDebut || null,
            dateFin: form.dateFin || null,
            anneeCampagne: Number(form.anneeCampagne),

            // ✅ Type dans le code (pas visible pour l'utilisateur)
            typeCampagne: isRecrutementModule ? "RECRUTEMENT" : isSkillsModule ? "SKILLS" : "FORMATION",

            filterMode,
            managerLevelFilter: managerLevelFilter || null,
            entiteIds: uniqNumbers(entiteIds),
            posteIds: uniqNumbers(posteIds),

            userIds: uniqNumbers((selectedUsers || []).map((u) => u.id)),

            // ✅ Formation uniquement
            catalogueIds: isFormationModule ? uniqNumbers(catalogueIds) : [],
            nombreMaxFormations: isFormationModule ? Number(form.nombreMaxFormations) : 1,

            // ✅ Recrutement uniquement
            recruitmentLimits: isRecrutementModule
                ? (recruitmentLimits || []).map((r) => ({
                    typeLimite: r.typeLimite,
                    limite: Number(r.limite),
                    entiteId: String(r.typeLimite).toLowerCase().includes("entit") ? Number(r.entiteId) : null,
                }))
                : [],

            validation: form.niveauValidation,

            envoiEmails: form.envoiEmails,
            dateEnvoiEmails: form.envoiEmails === "DIFFERE" ? (form.dateEnvoiEmails || null) : null,

            planificationRelance: String(form.planificationRelance) === "true",
            dateRelance: String(form.planificationRelance) === "true" ? (form.dateRelance || null) : null,
            dateRelance2: String(form.planificationRelance) === "true" ? (form.dateRelance2 || null) : null,
            dateRelance3: String(form.planificationRelance) === "true" ? (form.dateRelance3 || null) : null,

            axes,
            status: Boolean(form.status),

            budgets,
        };
    };

    const onSubmit = () => {
        if (!validate()) return;

        const payload = buildPayload();

        if (cid) dispatch(updateCampagneRequest(cid, payload));
        else dispatch(createCampagneRequest(payload));

        Swal.fire({
            icon: "success",
            text: cid ? "Campagne modifiée" : "Campagne ajoutée",
            timer: 1200,
            showConfirmButton: false,
        });

        navigate("/formation/responsable/campagnes");
    };

    const addAxe = () => {
        const v = String(axeToAdd || "").trim();
        if (!v) return;
        if (axesArray.includes(v)) {
            setAxeToAdd("");
            return;
        }
        setAxesArray((prev) => prev.concat([v]));
        setAxeToAdd("");
    };

    const removeAxe = (idx) => setAxesArray((prev) => prev.filter((_, i) => i !== idx));

    return (
        <div className="cpgPage">
            <UsersListModal
                filterMode={filterMode}
                managerlevel={managerLevelFilter}
                entites={entiteIds}
                postes={posteIds}
                opened={openUsersModal}
                liteusers={usersState}
                users={usersState}
                setUsers={(e) => setUsersState(e)}
                setSelectedUsers={(e) => setSelectedUsers(e)}
                closed={() => setOpenUsersModal(false)}
            />

            <div className="cpgContainer">
                <div className="cpgHeader">
                    <div>
                        <div className="cpgTitle">{cid ? "Modifier campagne" : "Créer une campagne"}</div>
                    </div>

                    <div style={{ display: "flex", gap: 10 }}>
                        <button className="cpgBtn cpgBtnLight" type="button" onClick={() => navigate("/formation/responsable/campagnes")}>
                            ← Retour
                        </button>
                        <button
                            className="cpgBtn cpgBtnPrimary"
                            type="button"
                            onClick={onSubmit}
                            disabled={campagnesLoading}
                        >
                            {campagnesLoading ? "Enregistrement..." : cid ? "Enregistrer" : "Créer"}
                        </button>
                    </div>
                </div>

                {/* Identification */}
                <div className="cpgCard">
                    <div className="cpgSectionTitle">Identification</div>

                    <div className="cpgGrid2">
                        <div className="cpgField">
                            <label>Nom *</label>
                            <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
                            {errors.nom && <div className="cpgError">{errors.nom}</div>}
                        </div>

                        <div className="cpgField">
                            <label>Référence</label>
                            <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                        </div>

                        <div className="cpgField" style={{ gridColumn: "1 / -1" }}>
                            <label>Description *</label>
                            <textarea
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                            />
                            {errors.description && <div className="cpgError">{errors.description}</div>}
                        </div>

                        <div className="cpgField">
                            <label>Date début</label>
                            <input
                                type="date"
                                value={form.dateDebut}
                                onChange={(e) => setForm({ ...form, dateDebut: e.target.value })}
                            />
                        </div>

                        <div className="cpgField">
                            <label>Date de fin</label>
                            <input
                                type="date"
                                value={form.dateFin}
                                onChange={(e) => setForm({ ...form, dateFin: e.target.value })}
                            />
                        </div>

                        <div className="cpgField">
                            <label>Année campagne</label>
                            <input
                                type="number"
                                value={form.anneeCampagne}
                                onChange={(e) => setForm({ ...form, anneeCampagne: e.target.value })}
                            />
                        </div>

                        {isFormationModule && !isSkillsModule && (
                            <div className="cpgField">
                                <label>Max de formations par collaborateur</label>
                                <input
                                    type="number"
                                    min={1}
                                    value={form.nombreMaxFormations}
                                    onChange={(e) => setForm({ ...form, nombreMaxFormations: e.target.value })}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {isRecrutementModule && (
                    <div className="cpgCard">
                        <div className="cpgSectionTitle">Limites de recrutement</div>
                        {errors.recruitmentLimits && <div className="cpgError">{errors.recruitmentLimits}</div>}

                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {(recruitmentLimits || []).map((r, idx) => (
                                <div key={idx} className="cpgGrid2" style={{ alignItems: "end" }}>
                                    <div className="cpgField">
                                        <label>Type limite</label>
                                        <select
                                            value={r.typeLimite}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setRecruitmentLimits((prev) =>
                                                    prev.map((x, i) =>
                                                        i === idx ? { ...x, typeLimite: v, entiteId: v === "Par entité" ? x.entiteId : "" } : x
                                                    )
                                                );
                                            }}
                                        >
                                            <option value="Global">Global</option>
                                            <option value="Par entité">Par entité</option>
                                        </select>
                                    </div>

                                    <div className="cpgField">
                                        <label>Entité</label>
                                        <select
                                            value={r.entiteId}
                                            disabled={r.typeLimite !== "Par entité"}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setRecruitmentLimits((prev) =>
                                                    prev.map((x, i) => (i === idx ? { ...x, entiteId: v } : x))
                                                );
                                            }}
                                        >
                                            <option value="">—</option>
                                            {(entites || []).map((en) => (
                                                <option key={en.id} value={en.id}>
                                                    {en.nom || en.name || `Entité #${en.id}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="cpgField">
                                        <label>Limite</label>
                                        <input
                                            type="number"
                                            min={0}
                                            value={r.limite}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setRecruitmentLimits((prev) =>
                                                    prev.map((x, i) => (i === idx ? { ...x, limite: v } : x))
                                                );
                                            }}
                                        />
                                    </div>

                                    <div className="cpgField" style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                                        <button
                                            type="button"
                                            className="cpgBtn cpgBtnPrimary"
                                            onClick={() =>
                                                setRecruitmentLimits((prev) => prev.concat([{ typeLimite: "Global", entiteId: "", limite: "" }]))
                                            }
                                        >
                                            + Ajouter
                                        </button>

                                        {recruitmentLimits.length > 1 && (
                                            <button
                                                type="button"
                                                className="cpgBtn cpgBtnOutlineDanger"
                                                onClick={() => setRecruitmentLimits((prev) => prev.filter((_, i) => i !== idx))}
                                            >
                                                Supprimer
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {isFormationModule && !isSkillsModule && (
                    <div className="cpgCard">
                        <div className="cpgSectionTitle">Catalogues</div>
                        {errors.catalogues && <div className="cpgError">{errors.catalogues}</div>}

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }}>
                            {(catalogues || []).map((c) => {
                                const active = catalogueIds.includes(Number(c.id));
                                const title = c.nom || c.name || `Catalogue #${c.id}`;
                                return (
                                    <div
                                        key={c.id}
                                        onClick={() => setCatalogueIds((prev) => toggleId(prev, c.id))}
                                        style={{
                                            border: `1px solid ${active ? "rgba(238,100,95,0.35)" : "var(--c-border)"}`,
                                            borderRadius: 16,
                                            padding: 12,
                                            cursor: "pointer",
                                            background: active ? "rgba(238,100,95,0.08)" : "#fff",
                                            fontWeight: 900,
                                        }}
                                    >
                                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                            <input
                                                type="checkbox"
                                                checked={active}
                                                onChange={() => setCatalogueIds((prev) => toggleId(prev, c.id))}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="cpgHint" style={{ marginTop: 10 }}>
                            {catalogueIds.length} catalogue(s) sélectionné(s)
                        </div>
                    </div>
                )}

                {/* Population & visibilité */}
                <div className="cpgCard">
                    <div className="cpgSectionTitle">Population & visibilité</div>

                    <div className="cpgGrid2">
                        <div className="cpgField">
                            <label>Filter mode</label>
                            <select value={filterMode} onChange={(e) => setFilterMode(e.target.value)}>
                                <option value="IN">IN (inclure)</option>
                                <option value="NOT_IN">NOT IN (exclure)</option>
                            </select>
                        </div>

                        <div className="cpgField">
                            <label>Niveau manager</label>
                            <select value={managerLevelFilter} onChange={(e) => setManagerLevelFilter(e.target.value)}>
                                <option value="">—</option>
                                <option value="n+1">n+1</option>
                                <option value="n+2">n+2</option>
                            </select>
                        </div>
                    </div>

                    <div className="cpgGrid2" style={{ marginTop: 12 }}>
                        <div className="cpgField">
                            <MultiSelectDropdown
                                label="Entités"
                                items={entites || []}
                                value={entiteIds}
                                onChange={setEntiteIds}
                                placeholder="Sélectionner des entités..."
                                getId={(x) => Number(x.id)}
                                getLabel={(x) => x.nom || x.name || `Entité #${x.id}`}
                            />
                            <div className="cpgHint" style={{ marginTop: 8 }}>
                                {entiteIds.length} entité(s) sélectionnée(s)
                            </div>
                        </div>

                        <div className="cpgField">
                            <MultiSelectDropdown
                                label="Postes"
                                items={jobs || []}
                                value={posteIds}
                                onChange={setPosteIds}
                                placeholder="Sélectionner des postes..."
                                getId={(x) => Number(x.id)}
                                getLabel={(x) => x.nom || x.name || `Poste #${x.id}`}
                            />
                            <div className="cpgHint" style={{ marginTop: 8 }}>
                                {posteIds.length} poste(s) sélectionné(s)
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                        <div>
                            <div style={{ fontWeight: 1000, color: "var(--c-text)" }}>
                                Users chargés : {populationCount} user(s)
                            </div>
                            <div className="cpgHint">{selectedLabel}</div>
                        </div>

                        <button
                            type="button"
                            className="cpgBtn cpgBtnPrimary"
                            onClick={() => setOpenUsersModal(true)}
                            disabled={!managerLevelFilter}
                            title={!managerLevelFilter ? "Choisis d’abord n+1 ou n+2" : ""}
                        >
                            Collaborateurs
                        </button>
                    </div>

                    {!!managerLevelFilter && selectedUsers.length === 0 && (
                        <div className="cpgHint" style={{ marginTop: 12 }}>
                            (Optionnel) Tu peux laisser vide → la visibilité reste basée sur les critères.
                        </div>
                    )}
                </div>

                {/* Validation */}
                <div className="cpgCard">
                    <div className="cpgSectionTitle">Niveau de validation</div>

                    <div className="cpgGrid2">
                        <div className="cpgField">
                            <label>Validation</label>
                            <select value={form.niveauValidation} onChange={(e) => setForm({ ...form, niveauValidation: e.target.value })}>
                                <option value="MANAGER">Manager</option>
                                <option value="RESPONSABLE_FORMATION">Responsable de formation</option>
                                <option value="BOTH">Les deux</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Budget */}
                {!isSkillsModule && (
                <div className="cpgCard">
                    <div className="cpgSectionTitle">Budget</div>

                    <div className="cpgGrid2">
                        <div className="cpgField">
                            <label>Type budget</label>
                            <select value={typeBudget} onChange={(e) => setTypeBudget(e.target.value)}>
                                <option value="Global">Global</option>
                                <option value="Par entité">Par entité</option>
                            </select>
                        </div>

                        <div className="cpgField">
                            <label>Devise</label>
                            <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                                <option value="MAD">MAD</option>
                                <option value="EUR">EUR</option>
                                <option value="USD">USD</option>
                            </select>
                        </div>
                    </div>

                    {typeBudget === "Global" && (
                        <div className="cpgField" style={{ marginTop: 12 }}>
                            <label>Budget global *</label>
                            <input
                                type="number"
                                min={0}
                                value={budgetGlobal}
                                onChange={(e) => setBudgetGlobal(e.target.value)}
                                placeholder="Ex: 100000"
                            />
                            {errors.budgetGlobal && <div className="cpgError">{errors.budgetGlobal}</div>}
                        </div>
                    )}

                    {typeBudget === "Par entité" && (
                        <div style={{ marginTop: 12 }}>
                            {errors.budgetsByEntite && <div className="cpgError">{errors.budgetsByEntite}</div>}

                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ fontWeight: 1000, color: "var(--c-text)" }}>Budgets par entité</div>
                                <button type="button" className="cpgBtn cpgBtnPrimary" onClick={addBudgetRow}>
                                    + Ajouter
                                </button>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
                                {(budgetsByEntite || []).map((row, idx) => (
                                    <div key={idx} className="cpgGrid2">
                                        <div className="cpgField">
                                            <label>Entité *</label>
                                            <select value={row.entiteId} onChange={(e) => updateBudgetRow(idx, { entiteId: e.target.value })}>
                                                <option value="">—</option>
                                                {(entites || []).map((en) => (
                                                    <option key={en.id} value={en.id}>
                                                        {en.nom || en.name || `Entité #${en.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="cpgField">
                                            <label>Montant *</label>
                                            <div style={{ display: "flex", gap: 10 }}>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={row.montant}
                                                    onChange={(e) => updateBudgetRow(idx, { montant: e.target.value })}
                                                    style={{ flex: 1 }}
                                                />
                                                <button type="button" className="cpgBtn cpgBtnOutlineDanger" onClick={() => removeBudgetRow(idx)}>
                                                    X
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                )}

                {/* Relances */}
                <div className="cpgCard">
                    <div className="cpgSectionTitle">Planifier relances</div>

                    <div className="cpgGrid2">
                        <div className="cpgField">
                            <label>Relances</label>
                            <select value={form.planificationRelance} onChange={(e) => setForm({ ...form, planificationRelance: e.target.value })}>
                                <option value="false">Non</option>
                                <option value="true">Oui</option>
                            </select>
                        </div>
                    </div>

                    {String(form.planificationRelance) === "true" && (
                        <div className="cpgGrid2" style={{ marginTop: 12 }}>
                            <div className="cpgField">
                                <label>Date relance 1 *</label>
                                <input type="date" value={form.dateRelance} onChange={(e) => setForm({ ...form, dateRelance: e.target.value })} />
                                {errors.dateRelance && <div className="cpgError">{errors.dateRelance}</div>}
                            </div>

                            <div className="cpgField">
                                <label>Date relance 2</label>
                                <input type="date" value={form.dateRelance2} onChange={(e) => setForm({ ...form, dateRelance2: e.target.value })} />
                            </div>

                            <div className="cpgField">
                                <label>Date relance 3</label>
                                <input type="date" value={form.dateRelance3} onChange={(e) => setForm({ ...form, dateRelance3: e.target.value })} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Axes */}
                <div className="cpgCard">
                    <div className="cpgSectionTitle">Axes stratégiques</div>

                    <div className="cpgGrid2">
                        <div className="cpgField">
                            <label>Ajouter un axe</label>
                            <div style={{ display: "flex", gap: 10 }}>
                                <input
                                    value={axeToAdd}
                                    onChange={(e) => setAxeToAdd(e.target.value)}
                                    placeholder="Ex: Leadership, Soft skills..."
                                    style={{ flex: 1 }}
                                />
                                <button type="button" className="cpgBtn cpgBtnPrimary" onClick={addAxe}>
                                    + Ajouter
                                </button>
                            </div>
                        </div>
                    </div>

                    {(axesArray || []).length > 0 ? (
                        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                            {axesArray.map((axe, idx) => (
                                <div
                                    key={`${axe}-${idx}`}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        padding: "10px 12px",
                                        borderRadius: 16,
                                        background: "rgba(17,24,39,0.04)",
                                        border: "1px solid var(--c-border)",
                                        fontWeight: 900,
                                    }}
                                >
                                    <div>{axe}</div>
                                    <button type="button" className="cpgBtn cpgBtnOutlineDanger" onClick={() => removeAxe(idx)}>
                                        Supprimer
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="cpgHint" style={{ marginTop: 12 }}>
                            Aucun axe ajouté.
                        </div>
                    )}
                </div>

                {/* Autres informations */}
                <div className="cpgCard">
                    <div className="cpgSectionTitle">Autres informations</div>

                    <div className="cpgGrid2">
                        <div className="cpgField">
                            <label>Envoi emails</label>
                            <select value={form.envoiEmails} onChange={(e) => setForm({ ...form, envoiEmails: e.target.value })}>
                                <option value="INSTANTANE">Instantané</option>
                                <option value="DIFFERE">Différé</option>
                                <option value="SANS_EMAIL">Sans email</option>
                            </select>
                        </div>

                        <div className="cpgField">
                            <label>Status</label>
                            <select value={String(form.status)} onChange={(e) => setForm({ ...form, status: e.target.value === "true" })}>
                                <option value="true">Actif</option>
                                <option value="false">Inactif</option>
                            </select>
                        </div>
                    </div>

                    {form.envoiEmails === "DIFFERE" && (
                        <div className="cpgField" style={{ marginTop: 12 }}>
                            <label>Date d’envoi emails *</label>
                            <input
                                type="date"
                                value={form.dateEnvoiEmails}
                                onChange={(e) => setForm({ ...form, dateEnvoiEmails: e.target.value })}
                            />
                            {errors.dateEnvoiEmails && <div className="cpgError">{errors.dateEnvoiEmails}</div>}
                        </div>
                    )}
                </div>

                {/* Footer actions */}
                <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                    <button className="cpgBtn cpgBtnLight" type="button" onClick={() => navigate("/campagnes")}>
                        Annuler
                    </button>
                    <button className="cpgBtn cpgBtnPrimary" type="button" onClick={onSubmit} disabled={campagnesLoading}>
                        {campagnesLoading ? "Enregistrement..." : cid ? "Enregistrer" : "Créer"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CampagneFormModal;
